import { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Avatar, Button, Alert as MuiAlert, Tabs, Tab, Badge } from '@mui/material';
import { Dashboard as DashboardIcon, Refresh as RefreshIcon, NotificationsActive } from '@mui/icons-material';

// DashboardV2 components
import {
  GlobalStatsRow,
  ClientCardsRow,
  DeviceTable,
  DeviceDetailDrawer,
} from './DashboardV2/components';

// MasterDashboard widgets
import {
  CategoryHealthCards,
  ActivityTimeline,
} from './components/dashboard';

// Alert components (from AlertsDashboard)
import { AlertStatusLight } from './components/AlertStatusLight';
import { AlertStatistics } from './components/AlertStatistics';
import { AlertsTable } from './components/AlertsTable';
import { ConnectionStatus } from './components/ConnectionStatus';

// DashboardV2 types
import { Device, DeviceFilters, ClientStats, GlobalStats, Client, AlertHistoryItem, StatFilter } from './DashboardV2/types';

// Hooks
import { useClientStats, useExporters, useAlerts } from './hooks';
import { useCategories } from './hooks/useCategories';
import { useWebsites } from './hooks/useWebsites';

// API types
import { ClientStats as ApiClientStats } from '../../../api/cansense/dashboardApi';
import { Exporter } from './types/exporter';
import { Website } from './types/website';

// ============================================================================
// Mapping functions (from DashboardV2)
// ============================================================================


function mapApiClientStatsToUiClientStats(apiClients: ApiClientStats[]): ClientStats[] {
  return apiClients.map((client) => ({
    id: client.customerId,
    name: client.customerName,
    shortName: generateShortName(client.customerName),
    total: client.total || 0,
    up: client.online || 0,
    down: client.offline || 0,
    warning: client.warning || 0,
    alerts: client.activeAlerts || 0,
  }));
}

function generateShortName(name: string): string {
  // If name contains a parenthetical like "Internal (SDS)", use the content in parens
  const parenMatch = name.match(/\(([^)]+)\)/);
  if (parenMatch) {
    return parenMatch[1].trim();
  }
  // For short names (≤10 chars), use as-is
  if (name.length <= 10) {
    return name;
  }
  // For longer names, abbreviate: first letter of each word
  const words = name.split(/[\s\-_]+/);
  if (words.length > 1) {
    return words.slice(0, 4).map((w) => w[0]).join('').toUpperCase();
  }
  return name.substring(0, 8);
}

function extractClientsFromStats(clientStats: ClientStats[]): Client[] {
  return clientStats.map((c) => ({ id: c.id, name: c.name, shortName: c.shortName }));
}

function formatLastSeen(timestamp: string | null | undefined): string {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

function mapExporterToDevice(exporter: Exporter): Device {
  const mapStatus = (status: string): 'up' | 'down' | 'warning' => {
    if (status === 'up') return 'up';
    if (status === 'down') return 'down';
    return 'warning';
  };

  return {
    id: exporter.id,
    name: exporter.name,
    displayName: exporter.display_name || exporter.name,
    clientId: exporter.customer_id || 0,
    client: exporter.customer_name || 'Internal',
    status: mapStatus(exporter.status),
    type: exporter.category_name || 'Infrastructure',
    category: exporter.category_name || exporter.prometheus_job || 'unknown',
    prometheusJob: exporter.prometheus_job,
    grafanaDashboardUid: exporter.grafana_dashboard_uid || null,
    host: exporter.target_host,
    lastSeen: formatLastSeen(exporter.last_scrape),
    collectionMethod: exporter.collection_method || 'pull',
    alertCount: 0,
  };
}

function mapWebsiteToDevice(website: Website): Device {
  const mapStatus = (status: string): 'up' | 'down' | 'warning' => {
    if (status === 'up') return 'up';
    if (status === 'down') return 'down';
    return 'warning';
  };

  return {
    id: -(website.id + 1000),
    name: website.url,
    displayName: website.name || website.url,
    clientId: website.customer_id || 0,
    client: website.customer_name || 'Internal',
    status: mapStatus(website.status),
    type: 'Web',
    category: 'website_monitor',
    prometheusJob: 'website_monitor',
    grafanaDashboardUid: null,
    host: website.url,
    lastSeen: formatLastSeen(website.last_check),
    alertCount: 0,
    url: website.url,
    websiteId: website.id,
    responseTimeMs: website.response_time_ms,
    sslExpiryDays: website.ssl_expiry_days,
    sslExpiryDate: website.ssl_expiry_date || null,
    uptimePercent: website.uptime_24h,
    lastError: website.last_error,
  };
}

function formatAlertTime(timestamp: string | null | undefined): string {
  if (!timestamp) return 'Unknown';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)} days ago`;
}

function calculateDuration(start: string | null | undefined, end: string | null | undefined): string {
  if (!start) return '-';
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ${diffMins % 60}m`;
  return `${Math.floor(diffHours / 24)}d ${diffHours % 24}h`;
}

// ============================================================================
// UnifiedDashboard Component
// ============================================================================

export function UnifiedDashboard() {
  // Data hooks
  const { clients: apiClients, loading: clientsLoading, error: clientsError, refetch: refetchClients } = useClientStats();
  const { exporters, loading: exportersLoading, error: exportersError, refetch: refetchExporters } = useExporters();
  const {
    alerts, suppressedAlerts, alertStatus, loading: alertsLoading, refreshData: refetchAlerts,
    acknowledgeAlert, dismissAlert, deleteAlert, restoreAlert, markReviewed, fetchSuppressedAlerts,
    isConnected, connectionState, dataSource, warning: alertWarning, error: alertError
  } = useAlerts();
  const { categories } = useCategories();
  const { websites, refetch: refetchWebsites } = useWebsites();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Filter state
  const [filters, setFilters] = useState<DeviceFilters>({ clientId: 'all', type: 'all', status: 'all', search: '' });
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [statFilter, setStatFilter] = useState<StatFilter>('all');

  // Computed: client stats
  const clientStats = useMemo(() => mapApiClientStatsToUiClientStats(apiClients), [apiClients]);
  const clients = useMemo(() => extractClientsFromStats(clientStats), [clientStats]);

  // Computed: alert counts per exporter (only non-resolved alerts)
  const alertCountByExporter = useMemo(() => {
    const counts: Record<number, number> = {};
    alerts.forEach((alert) => {
      const exporterId = alert.exporter_id;
      if (exporterId && alert.status !== 'resolved' && alert.status !== 'dismissed') {
        counts[exporterId] = (counts[exporterId] || 0) + 1;
      }
    });
    return counts;
  }, [alerts]);

  // Computed: devices (exporters + websites mapped to Device format)
  const devices = useMemo(() => {
    const exporterDevices = exporters.map((exporter) => {
      const device = mapExporterToDevice(exporter);
      device.alertCount = alertCountByExporter[exporter.id] || 0;
      return device;
    });
    const websiteDevices = websites
      .filter((w) => w.enabled)
      .map((website) => mapWebsiteToDevice(website));
    return [...exporterDevices, ...websiteDevices];
  }, [exporters, alertCountByExporter, websites]);

  // Computed: global stats from devices (consistent with what's displayed)
  const globalStats: GlobalStats = useMemo(() => {
    const activeAlertCount = alerts.filter(
      (a) => a.status !== 'resolved' && a.status !== 'dismissed'
    ).length;
    return {
      totalDevices: devices.length,
      upDevices: devices.filter((d) => d.status === 'up').length,
      downDevices: devices.filter((d) => d.status === 'down').length,
      warningDevices: devices.filter((d) => d.status === 'warning').length,
      totalAlerts: activeAlertCount,
    };
  }, [devices, alerts]);

  // Computed: filtered devices (respects both table filters and stat card filter)
  const filteredDevices = useMemo(() => {
    return devices.filter((device) => {
      if (filters.clientId !== 'all' && device.clientId !== filters.clientId) return false;
      if (filters.type !== 'all' && device.type !== filters.type) return false;
      if (filters.status !== 'all' && device.status !== filters.status) return false;
      // Stat card filter
      if (statFilter === 'up' && device.status !== 'up') return false;
      if (statFilter === 'down' && device.status !== 'down') return false;
      if (statFilter === 'alerts' && device.alertCount === 0) return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        return device.name.toLowerCase().includes(s) || device.displayName.toLowerCase().includes(s) || device.client.toLowerCase().includes(s) || device.host.toLowerCase().includes(s);
      }
      return true;
    });
  }, [devices, filters, statFilter]);

  // Computed: alert history for selected device
  const selectedDeviceAlertHistory: AlertHistoryItem[] = useMemo(() => {
    if (!selectedDevice) return [];
    return alerts
      .filter((alert) => alert.exporter_id === selectedDevice.id)
      .map((alert) => {
        const mapSeverity = (sev: string): 'critical' | 'warning' | 'info' => {
          if (sev === 'critical' || sev === 'error') return 'critical';
          if (sev === 'warning') return 'warning';
          return 'info';
        };
        const mapStatus = (st: string): 'active' | 'resolved' | 'acknowledged' => {
          if (st === 'new') return 'active';
          if (st === 'acknowledged') return 'acknowledged';
          return 'resolved';
        };
        const parseJsonField = (field: string | undefined): Record<string, string> => {
          if (!field) return {};
          try { return JSON.parse(field); } catch { return {}; }
        };
        return {
          id: alert.id,
          name: alert.alertname || 'Unknown Alert',
          severity: mapSeverity(alert.severity),
          status: mapStatus(alert.status),
          time: formatAlertTime(alert.created_at),
          duration: calculateDuration(alert.created_at, alert.ends_at),
          summary: alert.summary || alert.description || '',
          description: alert.description || '',
          timeline: {
            created: alert.created_at ? new Date(alert.created_at).toLocaleString() : '',
            createdAgo: formatAlertTime(alert.created_at),
            updated: alert.updated_at ? new Date(alert.updated_at).toLocaleString() : '',
            updatedAgo: formatAlertTime(alert.updated_at),
            started: alert.starts_at ? new Date(alert.starts_at).toLocaleString() : '',
            ended: alert.ends_at ? new Date(alert.ends_at).toLocaleString() : null,
          },
          labels: parseJsonField(alert.labels),
          annotations: parseJsonField(alert.annotations),
        };
      });
  }, [selectedDevice, alerts]);

  // Handlers
  const handleClientSelect = (clientId: number | 'all') => setFilters((prev) => ({ ...prev, clientId }));
  const handleFilterChange = (newFilters: Partial<DeviceFilters>) => setFilters((prev) => ({ ...prev, ...newFilters }));
  const handleDeviceClick = (device: Device) => { setSelectedDevice(device); setDrawerOpen(true); };
  const handleDrawerClose = () => { setDrawerOpen(false); setTimeout(() => setSelectedDevice(null), 300); };
  const handleStatClick = (filter: StatFilter) => {
    if (filter === 'alerts') {
      // Switch to alerts tab
      setActiveTab(1);
      setStatFilter('all');
    } else {
      setStatFilter(filter);
      // Clear the status dropdown filter so they don't conflict
      setFilters((prev) => ({ ...prev, status: 'all' }));
    }
  };
  const handleCategoryClick = (categoryName: string) => {
    setFilters((prev) => ({ ...prev, type: categoryName === 'all' ? 'all' : categoryName }));
  };


  const handleRefreshAll = useCallback(() => {
    refetchClients();
    refetchExporters();
    refetchAlerts();
    refetchWebsites();
  }, [refetchClients, refetchExporters, refetchAlerts, refetchWebsites]);

  const isLoading = clientsLoading || exportersLoading;
  const hasError = clientsError || exportersError;
  const errorMessage = clientsError || exportersError;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <DashboardIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">CANsense</Typography>
            <Typography variant="body2" color="text.secondary">
              Infrastructure monitoring & alerts
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ConnectionStatus
            isConnected={isConnected}
            connectionState={connectionState}
            onReconnect={refetchAlerts}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshAll} disabled={isLoading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Banner */}
      {hasError && (
        <MuiAlert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={handleRefreshAll}>Retry</Button>}>
          {errorMessage || 'Failed to load dashboard data. Please try again.'}
        </MuiAlert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<DashboardIcon />} iconPosition="start" label="Overview" />
        <Tab
          icon={<Badge badgeContent={alertStatus?.alerts?.new || 0} color="error"><NotificationsActive /></Badge>}
          iconPosition="start"
          label="Alerts"
        />
      </Tabs>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <>
          <GlobalStatsRow stats={globalStats} loading={exportersLoading} activeFilter={statFilter} onStatClick={handleStatClick} />
          <ClientCardsRow clients={clientStats} selectedClientId={filters.clientId} onClientSelect={handleClientSelect} loading={clientsLoading} />
          <CategoryHealthCards
            exporters={exporters}
            alertCounts={alertCountByExporter}
            categories={categories}
            loading={exportersLoading}
            selectedCategory={filters.type !== 'all' ? filters.type : undefined}
            onCategoryClick={handleCategoryClick}
          />
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' }, mb: 3 }}>
            <Box sx={{ flex: { lg: 2 }, minWidth: 0 }}>
              <DeviceTable
                devices={filteredDevices}
                filters={filters}
                clients={clients}
                onFilterChange={handleFilterChange}
                onDeviceClick={handleDeviceClick}
                loading={exportersLoading}
              />
            </Box>
            {!exportersLoading && !alertsLoading && (
              <Box sx={{ flex: { lg: 1 }, minWidth: 0 }}>
                <ActivityTimeline exporters={exporters} alerts={alerts} />
              </Box>
            )}
          </Box>
          <DeviceDetailDrawer device={selectedDevice} open={drawerOpen} onClose={handleDrawerClose} alertHistory={selectedDeviceAlertHistory} />
        </>
      )}

      {/* Alerts Tab */}
      {activeTab === 1 && (
        <>
          {alertError && alerts.length > 0 && (
            <MuiAlert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> {alertError}
              </Typography>
            </MuiAlert>
          )}
          {alertWarning && (
            <MuiAlert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Data Source:</strong> {alertWarning}
              </Typography>
            </MuiAlert>
          )}
          <Box sx={{ mb: 3 }}>
            <AlertStatusLight status={alertStatus} />
          </Box>
          <AlertStatistics alertStatus={alertStatus} sx={{ mb: 3 }} />
          <AlertsTable
            alerts={alerts}
            suppressedAlerts={suppressedAlerts}
            loading={alertsLoading}
            isConnected={isConnected}
            onAcknowledge={acknowledgeAlert}
            onDismiss={dismissAlert}
            onDelete={deleteAlert}
            onRestore={restoreAlert}
            onMarkReviewed={markReviewed}
            onFetchSuppressed={fetchSuppressedAlerts}
            onRefresh={refetchAlerts}
          />
        </>
      )}
    </Box>
  );
}

export default UnifiedDashboard;
